import { dateOnlyToUtcMidnight } from '@sharedlib/common/utils/date';
import { resolveDropTimes } from '@modules/calendar-app/utils/drop-frame.util';
// Common
import { CalendarTenantScopeService } from '@modules/calendar-app/services/calendar-tenant-scope.service';
import { CalendarPanelReturnStateService } from '@modules/calendar-app/services/calendar-panel-return-state.service';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  signal,
  Signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';

// Types
import { Calendar } from '@modules/calendar-app/types/calendar';
import { CalendarEvent } from '@modules/calendar-app/types/calendar-event';
import { CalendarType } from '@modules/calendar-app/types/calendar-type';
import { CalendarsFilters } from '@modules/calendar-app/types/calendars-filters';
import { type CalendarsListState } from '@modules/calendar-app/types/calendars-list-state';
import { EventsFilters } from '@modules/calendar-app/types/events-filters';
import { EventsListState } from '@modules/calendar-app/types/events-list-state';
import { ListState } from '@modules/calendar-app/types/list-state';
import { VirtualFolder } from '@modules/calendar-app/types/virtual-folder';
import { Tab } from '@modules/common/types/tab';
import { DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import { type CalendarCellClickEvent } from '@modules/full-calendar/types/calendar-cell-click-event';
import { type CalendarDropEvent } from '@modules/full-calendar/types/calendar-drop-event';
import { StateKey } from '@modules/settings/types/state-key';

// Services
import { CalendarsListStateService } from '@modules/calendar-app/services/calendars-list-state.service';
import { CalendarsService } from '@modules/calendar-app/services/calendars.service';
import { EventsService } from '@modules/calendar-app/services/events.service';
import { CalendarAppStateService } from '@modules/calendar-app/services/state.service';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { ModalService } from '@modules/modal/services/modal.service';
import { PopoverService } from '@frontendlib/components/features/popover/services/popover.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';

// RX
import { BehaviorSubject, combineLatest, merge, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';

// Utils
import { toSignal } from '@angular/core/rxjs-interop';
import { DateRangeCalendarEventsListStore } from '@modules/calendar-app/services/date-range-events-list.store';
import { EventsListStateService } from '@modules/calendar-app/services/events-list-state.service';
import { Application } from '@modules/common/types/application';
import { ListMode } from '@modules/common/types/ListMode';
import { isCreationAllowedInVirtualFolder } from '@modules/common/utils/virtual-folder';
import { justActivatedCalendarUid } from '@modules/calendar-app/utils/calendar-selection.util';
import { beginningOfPeriodInZone, calculateDateInZone } from '@modules/calendar-app/utils/display-zone-period.util';
import { Objects } from '@sharedlib/common/lang/Objects';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { Stitch } from '@modules/common/types/stitch';
import type { CalendarEventSummaryDto } from '@sharedlib/dtos/dto/calendar-app/event.dto';
import type { CalendarSummaryDto } from '@sharedlib/dtos/dto/calendar-app/calendar.dto';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';

declare module '@modules/settings/types/state' {
  export interface State {
    [StateKey.eventsDPListState]?: EventsListState;
    [StateKey.calendarsDPListState]?: CalendarsListState;
  }
}

@Component({
  selector: 'app-events-panel',
  templateUrl: './events-panel.component.html',
  styleUrls: ['./events-panel.component.less'],
  standalone: false,
  providers: [
    ...EventsListStateService.providers({
      stateKey: StateKey.eventsDPListState,
      withAdvancedSearch: true,
    }),
    ...CalendarsListStateService.providers({
      stateKey: StateKey.calendarsDPListState,
      withAdvancedSearch: true,
    }),
    DateRangeCalendarEventsListStore,
  ],
})
export class EventsPanelComponent implements OnInit, OnDestroy {
  @ViewChild('popoverFormTemplate', { static: true }) public popoverFormTemplate: TemplateRef<any>;

  // Titles are free to be rebranded, the `value` identifiers are not: they are `ListState` members, and
  // the template switches on them (`selectedTab === 'today' | 'events' | 'calendars'`) with the same type
  // CalendarAppStateService.getTabs() returns.
  //
  // The `events` tab is titled differently in the two sets, and deliberately. Under All Events it is one
  // of two VIEWS of the same events — a day grid or a flat list — so "List" names what distinguishes it.
  // Both strips call the events list "Events", as the main events list does (Johanna, 2026-11-01: "List"
  // became "Events" under All Events too).
  protected readonly eventsTabs: Tab<Exclude<ListState, 'tabs'>>[] = [
    { title: 'Calendar', value: 'today' },
    { title: 'Events', value: 'events' },
  ];
  protected readonly folderTabs: Tab<Exclude<ListState, 'tabs'>>[] = [
    { title: 'Calendars', value: 'calendars' },
    { title: 'Events', value: 'events' },
  ];
  protected tabs: Tab<Exclude<ListState, 'tabs'>>[] = this.eventsTabs;
  // Default to the Calendar (day-grid) tab, not List — the all-events view opens on Calendar.
  protected selectedTab: Exclude<ListState, 'tabs'> = 'today';
  protected readonly CalendarType = CalendarType;
  public contextMenuOpened: boolean;
  public events: CalendarEventSummaryDto[] = [];
  public hostWidth = 0;
  public calendarEvent: CalendarEvent;
  public formPopoverClose = new Subject<void>();
  public selectedFilter: VirtualFolder;
  public calendarsFilters: CalendarsFilters;

  protected readonly ListMode = ListMode;
  // Drives the Calendar (day) view's scroll position; bumped to "now" each time
  // the Calendar tab is opened so it lands on the current time.
  protected readonly calendarScrollToTime = signal<Date | undefined>(undefined);
  protected readonly popoverClose = new Subject<void>();
  private loadTodayTabEvents = new Subject<void>();
  private alive = new Subject<void>();
  private virtualEvent = new BehaviorSubject<CalendarEvent>(null);
  private calendars: Calendar[] = [];

  private readonly eventsListStateService = inject(EventsListStateService, { self: true });
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  /** The panel's own Calendar section: where its quick form creates (#2051). */
  protected readonly calendarTenantScope = inject(CalendarTenantScopeService, { optional: true });

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829) — see EventsFilters.applyListState.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;
  private readonly calendarsListStateService = inject(CalendarsListStateService, { self: true });
  private readonly appStateService = inject(CalendarAppStateService);
  private readonly searchService = inject(AdvancedSearchService);
  private readonly dateRangeCalendarEventsListStore = inject(DateRangeCalendarEventsListStore);
  private readonly modalService = inject(ModalService);
  private readonly listReturnState = inject(CalendarPanelReturnStateService, { optional: true });
  private viewRevision = 0;
  private restoredListFolder: VirtualFolder | null = null;

  public readonly selectedDateControl = new FormControl<Date>(this.dateRangeCalendarEventsListStore.selectedDate(), {
    nonNullable: true,
  });

  private readonly _syncStoreToControlEffect = effect((): void => {
    const storeDate = this.dateRangeCalendarEventsListStore.selectedDate();
    const controlDate = this.selectedDateControl.value;
    if (controlDate && controlDate.getTime() === storeDate.getTime()) {
      return;
    }
    this.selectedDateControl.setValue(storeDate, { emitEvent: false });
  });

  protected readonly isAdvancedSearchApplied: Signal<boolean> = toSignal(
    this.searchService.isAdvancedSearchApplied(Application.calendar),
    {
      initialValue: false,
    },
  );
  /**
   * Search-shaped: the shared query/markers, transient ids, and this app's form-only filters (location,
   * attendees) — nothing else reports those, so this pill must. The date range and `stitched` are excluded
   * on purpose: they round-trip from the list's filter menu and light that menu's funnel pill instead.
   */
  protected readonly isSearchApplied: Signal<boolean> = toSignal(
    this.searchService.isSearchApplied(Application.calendar),
    {
      initialValue: false,
    },
  );
  protected readonly selectedCalendarUids = toSignal(this.appStateService.getSelectedCalendarUids());
  protected readonly virtualFolder: Signal<VirtualFolder> = toSignal(this.appStateService.getVirtualFolder());
  // `handleCalendarsChange`'s own record of what it last wrote — see its comment for why it can't rely
  // solely on the `selectedCalendarUids` signal for the "previous selection" it diffs against. This is a
  // deliberate shadow of the state service's own storage, scoped to this component; any other caller
  // that needs to diff a picker's before/after selection (e.g. `justActivatedCalendarUid`'s other current
  // caller, `side-bar-calendars-tree.component.ts`) should keep its own local record the same way rather
  // than relying on this field or on a signal read from the async-scoped state service.
  private lastKnownSelectedCalendarUids: string[] | undefined;

  /**
   * Derived pages (Flagged/Follow Up/Snoozed/Archive/Trash) offer no creation form — DP-001. Derived
   * from the stored page rather than from `selectFilter`, so it cannot drift from the list's filters.
   */
  protected readonly creationAllowed: Signal<boolean> = computed(() =>
    isCreationAllowedInVirtualFolder(this.virtualFolder()),
  );
  protected readonly eventsFilters: Signal<EventsFilters> = toSignal(
    combineLatest([
      this.eventsListStateService.value(),
      this.searchService.getState(),
      this.appStateService.getVirtualFolder(),
      this.appStateService.getSelectedCalendarUids(),
      this.displayTimeZone$,
    ]).pipe(
      map(([list, search, folder, selectedCalendarUids, displayTimeZone]) => {
        return new EventsFilters({
          containersIds: selectedCalendarUids,
          withEmpty: selectedCalendarUids.length === 0,
          displayTimeZone,
        })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      }),
      takeUntil(this.alive),
    ),
  );
  protected readonly eventsListMode: Signal<ListMode> = computed(() => {
    if (this.isAdvancedSearchApplied()) {
      return ListMode.LIST;
    }

    if (
      this.virtualFolder() === 'all_events' ||
      // or when calendar (container) selected
      (Objects.isNullOrUndefined(this.virtualFolder()) && this.eventsFilters().containersIds.length > 0)
    ) {
      return ListMode.DATE_RANGE;
    }
    return ListMode.LIST;
  });

  constructor(
    private dpService: DynamicPanelService,
    private eventsService: EventsService,
    private calendarsService: CalendarsService,
    private popoverService: PopoverService,
    private injector: Injector,
  ) {}

  /**
   * Lifecycle
   */

  public ngOnInit() {
    // The 'events' tab always shows the full month (and month-based pagination), independent of the
    // shared calendar grid's day/week/month type. Named by value, not by label: it is titled "List" under
    // All Events and "Events" under a derived folder.
    this.dateRangeCalendarEventsListStore.setCalendarType(CalendarType.MONTH);
    this.calendarScrollToTime.set(new Date());

    this.loadTodayTabEvents
      .pipe(
        switchMap(() =>
          this.eventsService.search({
            ...this.eventsFilters(),
            // SBUX-829: hand over the PICKED day and let `format()` widen it in the ACCOUNT zone
            // (`rangeStart`/`rangeEnd`). Widening here with the browser-local `beginningOfDay`/`endOfDay`
            // asked for the DEVICE's day while `time_zone` said the account's — a cold-load capture showed
            // this panel requesting `2026-07-30T18:00Z → 2026-07-31T17:59Z` (Bishkek's day) on a Kyiv
            // account. Leaving `exactTimeRange` unset is what lets the shared widening do it.
            fromTime: this.selectedDateControl.value,
            toTime: this.selectedDateControl.value,
            // Expand recurring-event occurrences for the day grid, matching the main
            // calendar view's store (date-range-events-list.store). Without this, an
            // occurrence falling on the selected day is missing because the base
            // event's startTime is on a different date.
            withRecurrence: true,
            limit: 1000,
          }),
        ),
        map(({ items }) => items),
        switchMap((events: CalendarEvent[]) =>
          this.virtualEvent.pipe(
            map((virtualEvent: CalendarEvent) => (virtualEvent ? [...events, virtualEvent] : events)),
          ),
        ),
        takeUntil(this.alive),
      )
      .subscribe((events: CalendarEventSummaryDto[]) => (this.events = events));

    merge(
      this.appStateService.getRefreshAll(),
      this.eventsService.getRefreshRequired(),
      this.selectedDateControl.valueChanges,
      this.appStateService.getSelectedCalendarUids(),
    )
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.loadTodayTabEvents.next();
      });

    this.selectedDateControl.valueChanges.pipe(takeUntil(this.alive)).subscribe((date) => {
      if (!date) {
        return;
      }
      if (date.getTime() === this.dateRangeCalendarEventsListStore.selectedDate().getTime()) {
        return;
      }
      this.dateRangeCalendarEventsListStore.setSelectedDate(date, date);
      this.appStateService.navigateToDate(date);
      this.appStateService.calendarType.set(CalendarType.DAY);
    });

    this.calendarsService
      .search()
      .pipe(takeUntil(this.alive))
      .subscribe(({ items: calendars }) => {
        this.calendars = calendars;
      });

    combineLatest([
      this.calendarsListStateService.value(),
      this.searchService.getState(),
      this.appStateService.getVirtualFolder(),
      this.displayTimeZone$,
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState reads it to reckon its day windows in the account
        // zone (SBUX-829).
        this.calendarsFilters = new CalendarsFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });

    this.loadTodayTabEvents.next();
  }

  ngOnDestroy() {
    this.formPopoverClose.next();
    this.formPopoverClose.complete();
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  handleNewEvent(event = new CalendarEvent()) {
    this.modalService.openFullForm(event, this.injector);
  }

  protected openCalendar(summary: CalendarSummaryDto): void {
    this.calendarsService
      .getItem(summary.id)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((calendar) => {
        this.dpService.setFormItem(calendar);
      });
  }

  protected openEvent(summary: CalendarEventSummaryDto) {
    const origin = this.listReturnState?.captureOrigin(summary.id, this.selectedFilter, this.selectedTab);
    const viewRevision = this.viewRevision;
    this.eventsService
      .getItem(summary.id)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((event) => {
        if (viewRevision === this.viewRevision && this.selectedFilter === 'all_events' && this.selectedTab === 'events') {
          this.listReturnState?.prepareOpening(origin ?? null, event);
        }
        this.dpService.setFormItem(event);
      });
  }

  // Mirrors the main calendar view (full-calendar.component): clicking an event in
  // the Calendar (day) view opens it in a modal rather than the dynamic-panel form.
  protected openEventInModal(summary: CalendarEventSummaryDto): void {
    this.eventsService
      .getItem(summary.id)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((event) => {
        this.modalService.openFullForm(event, this.injector);
      });
  }

  protected handleDateClicked(cellEvent: CalendarCellClickEvent): void {
    // Target the best currently-selected calendar (mirrors the main calendar view).
    // Falling back to calendars[0] would land the event on a calendar that may be
    // filtered out of this panel's view, so it would vanish after creation.
    this.appStateService
      .getBestSelectedCalendarUid()
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((calendarUid) => {
        this.calendarEvent = CalendarEvent.fromCalendarCell(null, cellEvent.date, CalendarType.DAY);
        this.calendarEvent.calendarId = calendarUid ?? this.calendars?.[0]?.id;
        this.virtualEvent.next(this.calendarEvent);

        this.popoverService.create(cellEvent.originRef, {
          template: this.popoverFormTemplate,
          placement: 'left',
          innerShadow: false,
          arrow: true,
          trigger: 'click',
          showUntil: this.formPopoverClose,
          injector: this.injector,
        });
      });
  }

  protected handlePopoverFormClose(): void {
    this.virtualEvent.next(null);
    this.formPopoverClose.next();
  }

  protected handleDrop(event: CalendarDropEvent): void {
    // Same as handleDateClicked: pin the dropped event to a visible calendar so it
    // doesn't get filtered out of this panel's view after creation.
    this.appStateService
      .getBestSelectedCalendarUid()
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((calendarUid) => {
        // Resizing/moving an existing event on the grid arrives here as a
        // DragDataTypes.event drop. Update its times in place instead of opening
        // the creation popover (which duplicated the event). Mirrors the main
        // calendar view's handleCellDrop.
        if (DragDataTypes.event === event.dragData.type) {
          // Narrow the drag payload to event summaries via the itemType discriminator
          // instead of a type assertion, keeping the data type-safe.
          const eventSummaries = event.dragData.data.filter(
            (item): item is CalendarEventSummaryDto => 'itemType' in item && item.itemType === StitchType.EVENT,
          );
          // Remote events have organizer-controlled times, so they can't be edited here.
          const editableEvents = eventSummaries.filter((e) => !CalendarEvent.isRemoteSummary(e));
          if (editableEvents.length > 0) {
            // Every grid emits account-zone wall clocks; the API needs instants (or UTC dates
            // for all-day events). Resolve the same frame as the main calendar before saving.
            const { start, end } = resolveDropTimes(
              { start: event.newStart, end: event.newEnd },
              {
                allDay: editableEvents.every((item) => item.allDay),
                emitsAccountDayWallClock: true,
                timeZone: this.displayTimeZoneService.timeZone(),
              },
            );
            // An unchanged drop must not materialize a recurring occurrence as an exception.
            const timeChanged = editableEvents.some((item) => {
              // Summary DTOs represent all-day dates at local midnight; writes use UTC midnight.
              const originalStart = item.allDay ? dateOnlyToUtcMidnight(new Date(item.startTime)) : new Date(item.startTime);
              const originalEnd = item.allDay ? dateOnlyToUtcMidnight(new Date(item.endTime)) : new Date(item.endTime);
              return (start !== undefined && originalStart.getTime() !== start.getTime()) ||
                (end !== undefined && originalEnd.getTime() !== end.getTime());
            });
            if (!timeChanged) return;

            this.eventsService.bunchUpdate(
              { ids: editableEvents.map(({ id }) => id) },
              {
                startTime: start,
                endTime: end,
              },
              { tenantItems: editableEvents },
            );
          }

          return;
        }

        this.calendarEvent = CalendarEvent.fromCalendarCell(
          CalendarEvent.fromDragData(event.dragData),
          event.newStart,
          CalendarType.DAY,
        );
        this.calendarEvent.calendarId = calendarUid ?? this.calendars?.[0]?.id;
        this.virtualEvent.next(this.calendarEvent);

        this.popoverService.create(event.originRef, {
          template: this.popoverFormTemplate,
          placement: 'left',
          innerShadow: false,
          arrow: true,
          trigger: 'click',
          showUntil: this.formPopoverClose,
          injector: this.injector,
        });
      });
  }

  protected selectFilter(folder: VirtualFolder): void {
    if (folder !== this.selectedFilter) this.viewRevision++;
    if (!folder) return;
    if (folder === this.selectedFilter && this.restoredListFolder === folder) return;
    this.selectedFilter = folder;
    this.appStateService.setVirtualFolder(folder);
    const returnToList = this.listReturnState?.consume(folder) ?? false;
    // Route through onTabChange so the tab's date/scroll side effects run — setting selectedTab
    // directly only updates the [value] binding and skips them (the app-tabs valueChange fires on
    // user interaction, not on programmatic changes).
    if (folder === 'all_events') {
      this.tabs = this.eventsTabs;
      // Open the all-events view on the Calendar (day-grid) tab by default, not the List tab.
      this.onTabChange(returnToList ? 'events' : 'today');
      this.restoredListFolder = returnToList ? folder : null;
    } else if (folder === 'all_calendars') {
      this.onTabChange('calendars');
    } else {
      this.tabs = this.folderTabs;
      this.onTabChange('events');
    }
  }

  protected onTabChange(tab: Exclude<ListState, 'tabs'>): void {
    if (tab !== this.selectedTab) this.viewRevision++;
    this.restoredListFolder = null;
    this.selectedTab = tab;

    const today = new Date();
    if (tab === 'events') {
      // The flat list: land on today's month and scroll it to today.
      this.dateRangeCalendarEventsListStore.setSelectedDate(today, today);
    } else if (tab === 'today') {
      // Calendar (day) view: land on today and scroll to the current time.
      this.selectedDateControl.setValue(today);
      this.calendarScrollToTime.set(today);
    }
  }

  protected handleSearch(): void {
    this.popoverClose.next();
  }

  protected clearSearch(): void {
    // Only the search terms: the per-app date/stitched filters belong to the filters menu and its pill.
    this.searchService.resetSearchTerms();
    // The transient "View Contacts" ids are part of those terms, so the mode they opened ends here —
    // at the owner, independently of whether the folder-menu directive is mounted to notice.
    this.dpService.clearIdsSearchMode();
  }

  protected handleCancel(): void {
    this.popoverClose.next();
  }

  protected handleCalendarsChange(selectedCalendarUids: string | string[] | null): void {
    if (Array.isArray(selectedCalendarUids)) {
      // `this.selectedCalendarUids()` reflects the last *committed* selection, not necessarily the very
      // latest write: it's a signal over `getSelectedCalendarUids()`, which round-trips through
      // `spaceContextStore.contextId$` (an async `toObservable`) before the localStorage-backed value.
      // `lastKnownSelectedCalendarUids` tracks what this handler itself last wrote, so a second toggle
      // fired before that round trip settles still diffs against the correct previous selection instead
      // of a stale one.
      const previousIds = this.lastKnownSelectedCalendarUids ?? this.selectedCalendarUids() ?? [];
      const justActivated = justActivatedCalendarUid(previousIds, selectedCalendarUids);

      this.lastKnownSelectedCalendarUids = selectedCalendarUids;
      this.appStateService.setSelectedCalendarUids(selectedCalendarUids);
      if (justActivated) {
        this.appStateService.setLastActivatedCalendarUid(justActivated);
      }
    } else if (typeof selectedCalendarUids === 'string') {
      this.lastKnownSelectedCalendarUids = [selectedCalendarUids];
      this.appStateService.setSelectedCalendarUids([selectedCalendarUids]);
      this.appStateService.setLastActivatedCalendarUid(selectedCalendarUids);
    } else {
      this.lastKnownSelectedCalendarUids = [];
      this.appStateService.setSelectedCalendarUids([]);
    }
  }

  /** The day row's ‹ ›: the day before or after, in the account's zone, as a picked day in the month does. */
  protected stepDay(direction: 1 | -1): void {
    const localeId = this.dateRangeCalendarEventsListStore.localeId();
    const timeZone = this.dateRangeCalendarEventsListStore.displayTimeZone();
    const stepped = calculateDateInZone(this.selectedDateControl.value, direction, CalendarType.DAY, timeZone);
    this.selectedDateControl.setValue(beginningOfPeriodInZone(stepped, CalendarType.DAY, localeId, timeZone));
  }

  protected handleDatepickerActiveDateChange(date: Date): void {
    const localeId = this.dateRangeCalendarEventsListStore.localeId();
    const timeZone = this.dateRangeCalendarEventsListStore.displayTimeZone();
    const firstOfMonth = beginningOfPeriodInZone(date, CalendarType.MONTH, localeId, timeZone);
    if (firstOfMonth.getTime() === this.dateRangeCalendarEventsListStore.fromTime().getTime()) {
      return;
    }
    this.dateRangeCalendarEventsListStore.setSelectedDate(firstOfMonth);
  }

  /** "Add details" on the quick form: continue in the full form with what was typed. */
  protected handleNewCalendar(calendar: Stitch): void {
    this.modalService.openFullForm(calendar, this.injector);
  }
}
